import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Calendar, Tag, Truck, 
  CheckCircle, XCircle, Percent, Package
} from 'lucide-react';

const AdminPromos = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    promo_type: 'nxm',
    buy_quantity: 6,
    pay_quantity: 3,
    applies_to_size: 40,
    buy_size_ml: 100,
    free_pocket_size_ml: 40,
    free_pocket_qty: 1,
    gift_mini_qty: 1,
    exclusive: true,
    shipping_free_city: 'Querétaro',
    shipping_cost_other: 99,
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    loadPromos();
  }, []);

  const loadPromos = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth('/api/admin/promos');
      if (res.ok) {
        const data = await res.json();
        setPromos(data.promos);
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      promo_type: 'nxm',
      buy_quantity: 6,
      pay_quantity: 3,
      applies_to_size: 40,
      buy_size_ml: 100,
      free_pocket_size_ml: 40,
      free_pocket_qty: 1,
      gift_mini_qty: 1,
      exclusive: true,
      shipping_free_city: 'Querétaro',
      shipping_cost_other: 99,
      start_date: '',
      end_date: '',
      is_active: true
    });
    setEditingPromo(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      name: promo.name,
      description: promo.description || '',
      promo_type: promo.promo_type,
      buy_quantity: promo.buy_quantity,
      pay_quantity: promo.pay_quantity,
      applies_to_size: promo.applies_to_size,
      buy_size_ml: promo.buy_size_ml ?? 100,
      free_pocket_size_ml: promo.free_pocket_size_ml ?? 40,
      free_pocket_qty: promo.free_pocket_qty ?? 1,
      gift_mini_qty: promo.gift_mini_qty ?? 1,
      exclusive: promo.exclusive ?? true,
      shipping_free_city: promo.shipping_free_city,
      shipping_cost_other: promo.shipping_cost_other,
      start_date: promo.start_date ? new Date(promo.start_date).toISOString().slice(0, 16) : '',
      end_date: promo.end_date ? new Date(promo.end_date).toISOString().slice(0, 16) : '',
      is_active: promo.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      let res;
      if (editingPromo) {
        res = await fetchWithAuth(`/api/admin/promos/${editingPromo.id}`, {
          method: 'PUT',
          body: payload
        });
      } else {
        res = await fetchWithAuth('/api/admin/promos', {
          method: 'POST',
          body: payload
        });
      }

      if (res.ok) {
        toast.success(editingPromo ? 'Promoción actualizada' : 'Promoción creada');
        setShowModal(false);
        resetForm();
        loadPromos();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar promoción');
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta promoción?')) return;

    try {
      const res = await fetchWithAuth(`/api/admin/promos/${promoId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Promoción eliminada');
        loadPromos();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (promo) => {
    try {
      const res = await fetchWithAuth(`/api/admin/promos/${promo.id}`, {
        method: 'PUT',
        body: { is_active: !promo.is_active }
      });

      if (res.ok) {
        toast.success(promo.is_active ? 'Promoción desactivada' : 'Promoción activada');
        loadPromos();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isBundle = (promo) => promo.promo_type === 'bundle_gift';

  const getPromoLabel = (promo) => {
    if (isBundle(promo)) {
      // "5x3": compra 3, te llevas 3 + los regalos
      return `${promo.buy_quantity + (promo.free_pocket_qty || 0) + (promo.gift_mini_qty || 0)}x${promo.buy_quantity}`;
    }
    return `${promo.buy_quantity}x${promo.pay_quantity}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-promos">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Promociones</h2>
          <p className="text-gray-400 text-sm">Gestiona las promociones de tu tienda</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
        >
          <Plus size={18} />
          Nueva Promoción
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
        <h3 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
          <Tag size={18} />
          ¿Cómo funcionan las promociones?
        </h3>
        <ul className="text-sm text-gray-300 space-y-1">
          <li>• <strong>Tipo NxM</strong>: El cliente compra N productos y paga solo M (ej: 6x3 = compra 6, paga 3)</li>
          <li>• <strong>Tipo Regalos</strong>: Al comprar N perfumes de 100ml, el de 35ml que ya agregó sale gratis y elige un 10ml de regalo (se anota en el pedido). Excluye a las demás promos.</li>
          <li>• <strong>Por presentación</strong>: Aplica solo a productos de 35ml o 100ml según configures</li>
          <li>• <strong>Envío especial</strong>: Durante la promo puedes ofrecer envío gratis a una ciudad específica</li>
          <li>• <strong>Fechas</strong>: La promo solo aplica dentro del rango de fechas configurado</li>
        </ul>
      </div>

      {/* Promos List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : promos.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-xl">
          <Tag size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No hay promociones creadas</p>
          <button
            onClick={openCreateModal}
            className="mt-4 text-amber-400 hover:text-amber-300"
          >
            Crear primera promoción
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {promos.map(promo => (
            <div 
              key={promo.id}
              className={`bg-gray-800 rounded-xl p-5 border-l-4 ${
                promo.is_currently_active 
                  ? 'border-green-500' 
                  : promo.is_active 
                  ? 'border-amber-500' 
                  : 'border-gray-600'
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Promo Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-bold">
                      {getPromoLabel(promo)}
                    </span>
                    <h3 className="text-lg font-medium text-white">{promo.name}</h3>
                    {promo.is_currently_active && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium animate-pulse">
                        ACTIVA AHORA
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Aplica a</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <Package size={14} />
                        {(() => { const sz = isBundle(promo) ? promo.buy_size_ml : promo.applies_to_size; return sz === 40 ? 35 : sz; })()}ml
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Envío gratis</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <Truck size={14} />
                        {promo.shipping_free_city}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Envío resto</p>
                      <p className="text-white font-medium">${promo.shipping_cost_other}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Estado</p>
                      <p className={`font-medium flex items-center gap-1 ${promo.is_active ? 'text-green-400' : 'text-red-400'}`}>
                        {promo.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {promo.is_active ? 'Activa' : 'Inactiva'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      Inicio: {formatDate(promo.start_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      Fin: {formatDate(promo.end_date)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(promo)}
                    className={`p-2 rounded-lg transition-colors ${
                      promo.is_active 
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                    title={promo.is_active ? 'Desactivar' : 'Activar'}
                  >
                    {promo.is_active ? <XCircle size={18} /> : <CheckCircle size={18} />}
                  </button>
                  <button
                    onClick={() => openEditModal(promo)}
                    className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(promo.id)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">
                {editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nombre de la promoción</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Hot Sale 6x3 (35ml)"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Descripción (interno)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Notas internas sobre esta promo"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Tipo de promoción */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Tipo de promoción</label>
                <div className="flex gap-3">
                  {[
                    { value: 'nxm', label: 'NxM (compra N, paga M)' },
                    { value: 'bundle_gift', label: 'Regalos (35ml gratis + 10ml)' }
                  ].map(opt => (
                    <label
                      key={opt.value}
                      className={`flex-1 text-center p-3 rounded-lg border-2 cursor-pointer text-sm transition-all ${
                        formData.promo_type === opt.value
                          ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                          : 'border-gray-600 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="promo_type"
                        value={opt.value}
                        checked={formData.promo_type === opt.value}
                        onChange={() => setFormData({...formData, promo_type: opt.value})}
                        className="hidden"
                        data-testid={`promo-type-${opt.value}`}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {formData.promo_type === 'bundle_gift' && (
                <div className="space-y-4" data-testid="bundle-config">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Cliente compra (perfumes de 100ml)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.buy_quantity}
                        onChange={(e) => setFormData({...formData, buy_quantity: parseInt(e.target.value) || 1})}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">35ml gratis por cada grupo</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.free_pocket_qty}
                        onChange={(e) => setFormData({...formData, free_pocket_qty: parseInt(e.target.value) || 0})}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Solo si el cliente ya lo agregó al carrito</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">10ml de regalo por cada grupo</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.gift_mini_qty}
                        onChange={(e) => setFormData({...formData, gift_mini_qty: parseInt(e.target.value) || 0})}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">El cliente lo elige (obligatorio). Sale del stock 10ml.</p>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.exclusive}
                      onChange={(e) => setFormData({...formData, exclusive: e.target.checked})}
                      className="mt-1 w-5 h-5 rounded bg-gray-700 border-gray-600 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-300">
                      <strong>Exclusiva</strong>: si el carrito califica, no se aplica ninguna otra promoción (recomendado, evita acumular descuentos)
                    </span>
                  </label>

                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                    <span className="text-amber-400 font-bold text-lg">
                      {formData.buy_quantity + (formData.free_pocket_qty || 0) + (formData.gift_mini_qty || 0)}x{formData.buy_quantity}
                    </span>
                    <span className="text-gray-300 ml-2">
                      Compra {formData.buy_quantity} de 100ml (precio normal) y llévate {formData.free_pocket_qty} de 35ml + {formData.gift_mini_qty} de 10ml gratis
                    </span>
                  </div>
                </div>
              )}

              {/* NxM Configuration */}
              {formData.promo_type === 'nxm' && (<>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Cliente compra</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.buy_quantity}
                      onChange={(e) => setFormData({...formData, buy_quantity: parseInt(e.target.value) || 1})}
                      min="1"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                      required
                    />
                    <span className="text-gray-400">productos</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Cliente paga</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.pay_quantity}
                      onChange={(e) => setFormData({...formData, pay_quantity: parseInt(e.target.value) || 1})}
                      min="1"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                      required
                    />
                    <span className="text-gray-400">productos</span>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                <span className="text-amber-400 font-bold text-lg">
                  {formData.buy_quantity}x{formData.pay_quantity}
                </span>
                <span className="text-gray-300 ml-2">
                  Compra {formData.buy_quantity}, paga {formData.pay_quantity}
                </span>
              </div>

              {/* Applies to size */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Aplica a presentación</label>
                <div className="flex gap-4">
                  <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.applies_to_size === 40 
                      ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                      : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}>
                    <input
                      type="radio"
                      name="applies_to_size"
                      value="40"
                      checked={formData.applies_to_size === 40}
                      onChange={() => setFormData({...formData, applies_to_size: 40})}
                      className="hidden"
                    />
                    <Package size={18} />
                    35ml
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.applies_to_size === 100 
                      ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                      : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}>
                    <input
                      type="radio"
                      name="applies_to_size"
                      value="100"
                      checked={formData.applies_to_size === 100}
                      onChange={() => setFormData({...formData, applies_to_size: 100})}
                      className="hidden"
                    />
                    <Package size={18} />
                    100ml
                  </label>
                </div>
              </div>
              </>)}

              {/* Shipping */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Envío gratis para</label>
                  <input
                    type="text"
                    value={formData.shipping_free_city}
                    onChange={(e) => setFormData({...formData, shipping_free_city: e.target.value})}
                    placeholder="Ciudad"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Costo envío resto del país</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={formData.shipping_cost_other}
                      onChange={(e) => setFormData({...formData, shipping_cost_other: parseFloat(e.target.value) || 0})}
                      min="0"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-8 pr-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Fecha de inicio</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Fecha de fin</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-5 h-5 rounded bg-gray-700 border-gray-600 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-white">Promoción activa</span>
              </label>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
                >
                  {editingPromo ? 'Guardar Cambios' : 'Crear Promoción'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromos;
